import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardAPI } from 'api/dashboard';
import { fiveMinutes } from './constants';
const DASHBOARDS_QUERY_KEY = [
    'dashboards'
];
const useDashboardsQuery = (options = {})=>{
    return useQuery({
        staleTime: fiveMinutes,
        ...options,
        queryKey: DASHBOARDS_QUERY_KEY,
        queryFn: ()=>DashboardAPI.getAllDashboards()
    });
};
const DASHBOARD_VIEWS_QUERY_KEY = 'DASHBOARD_VIEWS_QUERY';
export const getDashboardViewsQueryKey = ()=>[
        DASHBOARD_VIEWS_QUERY_KEY
    ];
const getDashboardQueryKey = (dashboardId)=>[
        'dashboards',
        `${dashboardId}`
    ];
const getErrorStatus = (error)=>{
    const response = error === null || error === void 0 ? void 0 : error.response;
    if (response && typeof response === 'object') {
        const { status } = response;
        return typeof status === 'number' ? status : undefined;
    }
    return undefined;
};
const useDashboardQuery = (dashboardId)=>{
    return useQuery(getDashboardQueryKey(dashboardId), ()=>DashboardAPI.getDashboard(dashboardId), {
        enabled: !!dashboardId && dashboardId !== -1,
        retry: (failureCount, error)=>{
            if (getErrorStatus(error) === 403) {
                return false;
            }
            return failureCount < 3;
        }
    });
};
const getDashboardQueryKeyV2 = (dashboardId)=>[
        'dashboards-v2',
        `${dashboardId}`
    ];
const useDashboardQueryV2 = (dashboardId)=>{
    return useQuery(getDashboardQueryKeyV2(dashboardId), ()=>DashboardAPI.getDashboardV2(dashboardId), {
        enabled: !!dashboardId && dashboardId !== -1,
        retry: (failureCount, error)=>{
            if (getErrorStatus(error) === 403) {
                return false;
            }
            return failureCount < 3;
        }
    });
};
const useCreateDashboardMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>DashboardAPI.createDashboard(payload), {
        onSuccess: (newDashboard, variables)=>{
            var _newDashboard_restrictedIds;
            const merged = {
                ...newDashboard,
                restrictedIds: (_newDashboard_restrictedIds = newDashboard.restrictedIds) !== null && _newDashboard_restrictedIds !== void 0 ? _newDashboard_restrictedIds : variables.restrictedIds
            };
            queryClient.setQueryData(DASHBOARDS_QUERY_KEY, (old = [])=>[
                    ...old,
                    merged
                ]);
            queryClient.setQueryData(getDashboardQueryKeyV2(merged.dashboardId), merged);
        }
    });
};
const predicateById = (dashboardId)=>(dashboard)=>dashboard.dashboardId === dashboardId;
const filterExceptId = (dashboardId)=>(dashboard)=>dashboard.dashboardId !== dashboardId;
const updateDashboard = (newDashboard, restrictedIds)=>(old = [])=>{
        const idx = old.findIndex(predicateById(newDashboard.dashboardId));
        if (idx === -1) {
            return old;
        }
        return old.map((d)=>{
            var _newDashboard_restrictedIds;
            var _newDashboard_widgets;
            if (d.dashboardId !== newDashboard.dashboardId) {
                return d;
            }
            return {
                ...newDashboard,
                widgets: ((_newDashboard_widgets = newDashboard.widgets) === null || _newDashboard_widgets === void 0 ? void 0 : _newDashboard_widgets.length) ? newDashboard.widgets : d.widgets,
                restrictedIds: (_newDashboard_restrictedIds = newDashboard.restrictedIds) !== null && _newDashboard_restrictedIds !== void 0 ? _newDashboard_restrictedIds : restrictedIds
            };
        });
    };
const deleteDashboard = (idToBeDeleted)=>(old = [])=>{
        return old.filter(filterExceptId(idToBeDeleted));
    };
const useUpdateDashboardMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation(({ dashboardId, ...rest })=>DashboardAPI.updateDashboard(dashboardId, rest), {
        onSuccess: (newDashboard, variables)=>{
            var _newDashboard_restrictedIds;
            const merged = {
                ...newDashboard,
                restrictedIds: (_newDashboard_restrictedIds = newDashboard.restrictedIds) !== null && _newDashboard_restrictedIds !== void 0 ? _newDashboard_restrictedIds : variables.restrictedIds
            };
            queryClient.setQueryData(DASHBOARDS_QUERY_KEY, updateDashboard(merged));
            queryClient.setQueryData(getDashboardQueryKeyV2(merged.dashboardId), (oldDashboard)=>{
                var _merged_widgets;
                if (!oldDashboard) {
                    return merged;
                }
                return {
                    ...merged,
                    widgets: ((_merged_widgets = merged.widgets) === null || _merged_widgets === void 0 ? void 0 : _merged_widgets.length) ? merged.widgets : oldDashboard.widgets
                };
            });
        }
    });
};
const useUpdateDashboardLayoutMutation = ()=>{
    return useMutation(({ dashboardId, layout })=>DashboardAPI.updateDashboardLayout(dashboardId, layout), {});
};
const useDuplicateDashboardMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((payload)=>DashboardAPI.duplicateDashboard(payload), {
        onSuccess: (newDashboard, variables)=>{
            var _newDashboard_restrictedIds;
            const merged = {
                ...newDashboard,
                restrictedIds: (_newDashboard_restrictedIds = newDashboard.restrictedIds) !== null && _newDashboard_restrictedIds !== void 0 ? _newDashboard_restrictedIds : variables.restrictedIds
            };
            queryClient.setQueryData(DASHBOARDS_QUERY_KEY, (old = [])=>[
                    ...old,
                    merged
                ]);
            queryClient.setQueryData(getDashboardQueryKeyV2(merged.dashboardId), merged);
        }
    });
};
const useDeleteDashboardMutation = ()=>{
    const queryClient = useQueryClient();
    return useMutation((dashboardId)=>DashboardAPI.deleteDashboard(dashboardId), {
        onSuccess: (_, dashboardId)=>{
            queryClient.setQueryData(DASHBOARDS_QUERY_KEY, deleteDashboard(dashboardId));
        }
    });
};
const useInitDashboardMutation = ()=>{
    return useMutation(()=>DashboardAPI.init());
};
export { DASHBOARDS_QUERY_KEY, useDashboardsQuery, getDashboardQueryKey, useDashboardQuery, useCreateDashboardMutation, useInitDashboardMutation, useDeleteDashboardMutation, useUpdateDashboardMutation, useDuplicateDashboardMutation, useUpdateDashboardLayoutMutation, useDashboardQueryV2, getDashboardQueryKeyV2, getErrorStatus };
